import React, { useCallback, useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import anime from "animejs";
import { attemptFullscreenToggle } from "../../../../utilities";
import LoaderController from "./LoaderController";
import {
  setLoaderComplete,
  setLoaderFadedOut,
  setLoaderFadeMidpoint,
} from "../../../../State/Slices/loadingProgressSlice";

const Loader = () => {
  const { dimensions, uiSymbolHeight, device, orientation, isMobileSmall } =
    useDimensions();
  const loadingProgress = useSelector((state) => state.loadingProgress);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const loaderControllerRef = useRef(null);
  const fadeOutStartedRef = useRef(false);
  const errorShownRef = useRef(false);
  const handleBackgroundClickRef = useRef(null);

  // Background click: fullscreen only (mirrors IntroPopupComposed)
  const handleBackgroundClick = useCallback(
    async (event) => {
      if (event?.stopPropagation) event.stopPropagation();
      await attemptFullscreenToggle(dispatch, device);
    },
    [dispatch, device],
  );
  handleBackgroundClickRef.current = handleBackgroundClick;

  // Initialize spine controller on mount
  useEffect(() => {
    if (
      containerRef.current &&
      !loaderControllerRef.current &&
      dimensions &&
      uiSymbolHeight
    ) {
      loaderControllerRef.current = new LoaderController(
        containerRef.current,
        dimensions,
        uiSymbolHeight,
        { device, orientation, isMobileSmall }
      );
      loaderControllerRef.current.setBackgroundClickHandler((...args) => {
        if (handleBackgroundClickRef.current) {
          handleBackgroundClickRef.current(...args);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update spine when dimensions change
  useEffect(() => {
    if (loaderControllerRef.current) {
      loaderControllerRef.current.updateDimensions(
        dimensions,
        uiSymbolHeight,
        { device, orientation, isMobileSmall }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, uiSymbolHeight, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loaderControllerRef.current) {
        loaderControllerRef.current.cleanup();
        loaderControllerRef.current = null;
      }
    };
  }, []);

  // Handle loading error — show error fallback UI
  useEffect(() => {
    if (
      loadingProgress.status === "error" &&
      loaderControllerRef.current &&
      !errorShownRef.current
    ) {
      errorShownRef.current = true;
      loaderControllerRef.current.showError();
    }
  }, [loadingProgress.status]);

  // When loading completes → wait for at least one full spine loop → mount game → settle → fade out
  // Mirrors the primary loader's lifecycle for consistent behavior
  useEffect(() => {
    if (
      loadingProgress.status === "success" &&
      containerRef.current &&
      !fadeOutStartedRef.current
    ) {
      fadeOutStartedRef.current = true;

      const controller = loaderControllerRef.current;
      if (!controller) {
        // No controller — skip animation, just mount and hide.
        // Still emit BOTH loader lifecycle signals: there's no fade tween
        // here to emit them from, and anything waiting on them would other-
        // wise hang forever (the standalone replay link's Enter modal is
        // gated on the midpoint — without this it would never open, leaving
        // a replay-locked page with no way to start the round).
        if (containerRef.current) containerRef.current.visible = false;
        dispatch(setLoaderComplete());
        dispatch(setLoaderFadeMidpoint());
        dispatch(setLoaderFadedOut());
        return;
      }

      // Step 1: Wait for at least one full spine loop to finish
      controller.onLoadingComplete().then(() => {
        if (!containerRef.current) return;

        // Step 2: First loop done — mount the game
        dispatch(setLoaderComplete());

        // Step 3: Wait for game to settle (count stable frames after mount)
        const SETTLE_FRAMES = 10;
        let stableCount = 0;

        const waitForSettle = () => {
          stableCount++;
          if (stableCount < SETTLE_FRAMES) {
            requestAnimationFrame(waitForSettle);
            return;
          }

          // Step 4: Game has settled — fade out the loader
          if (!containerRef.current) return;
          let midpointFired = false;
          anime({
            targets: containerRef.current,
            alpha: [1, 0],
            duration: 300,
            easing: "easeOutQuad",
            // Halfway through the fade — the game underneath is already
            // half-revealed. Anything that should appear AS the loader
            // leaves (rather than after it, which reads as a gap) hooks
            // this instead of setLoaderFadedOut below. Tied to the real
            // animation's progress, not a guessed delay, so it stays
            // correct if the fade duration ever changes.
            update: (anim) => {
              if (midpointFired || anim.progress < 50) return;
              midpointFired = true;
              dispatch(setLoaderFadeMidpoint());
            },
            complete: () => {
              if (containerRef.current) {
                containerRef.current.visible = false;
              }
              if (loaderControllerRef.current) {
                loaderControllerRef.current.cleanup();
                loaderControllerRef.current = null;
              }
              // Signal that the loader is visually gone
              dispatch(setLoaderFadedOut());
            },
          });
        };

        requestAnimationFrame(waitForSettle);
      });
    }
  }, [loadingProgress.status, dispatch]);

  return (
    <Container
      name="secondary_loader"
      ref={containerRef}
      x={0}
      y={0}
      zIndex={10001}
      sortableChildren
    />
  );
};

export default Loader;
